import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import ParkingExitDialog from '../components/parking/ParkingExitDialog';
import ParkingEntryDialog from '../components/parking/ParkingEntryDialog';
import { formatCurrency, formatDuration, platePlaceholder, getExitChargeLabel } from '../lib/parkingUtils';
import { shouldStartOcrCountdown, OCR_REQUEST_TIMEOUT_MS } from '../lib/ocrAutoGate';
import {
  CAMERA_OPTIONS,
  DEFAULT_CAMERA,
  requestCameraStream,
  requestMobileCameraStream,
  stopCameraStream,
} from '../lib/camera';
import { capturePlateRoiFromVideo } from '../lib/plateRoiCapture';
import {
  Car, LogIn, LogOutIcon, Camera, CameraOff, X, Loader2,
  Maximize2, ScanLine, Zap, QrCode, Settings, Bike
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const DISRUPTIVE_LOGO = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/qx3y7mcn_Recurso%202lgo%20disr.png";

const parkingChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('parking_events') : null;

/* ─────────── Camera Lane ─────────── */
function CameraLane({
  laneId,
  defaultRole,
  onPlateAction,
  insidePlates,
  ocrBlockedPlatesRef,
  ocrInterval,
  ocrSettings,
  isMobile,
  ocrEngineStatus,
}) {
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const ocrIntervalRef = useRef(null);
  const isOcrRunningRef = useRef(false);
  const autoOCRRef = useRef(false);
  const roleRef = useRef(defaultRole);
  const onPlateActionRef = useRef(onPlateAction);
  const insidePlatesRef = useRef(insidePlates);
  const ocrSettingsRef = useRef(ocrSettings);

  const [selectedCamera, setSelectedCamera] = useState(DEFAULT_CAMERA);
  const [role, setRole] = useState(defaultRole);
  const [isStreaming, setIsStreaming] = useState(false);
  const [autoOCR, setAutoOCR] = useState(false);
  const [isOcrRunning, setIsOcrRunning] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [detectedPlate, setDetectedPlate] = useState('');
  const [detectedConf, setDetectedConf] = useState(0);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctedPlate, setCorrectedPlate] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);
  const pendingPlateRef = useRef(null);
  const [mobileOcrResult, setMobileOcrResult] = useState(null);
  const [mobileEditPlate, setMobileEditPlate] = useState('');

  useEffect(() => { autoOCRRef.current = autoOCR; }, [autoOCR]);
  useEffect(() => { roleRef.current = role; }, [role]);
  useEffect(() => { onPlateActionRef.current = onPlateAction; }, [onPlateAction]);
  useEffect(() => { insidePlatesRef.current = insidePlates; }, [insidePlates]);
  useEffect(() => { ocrSettingsRef.current = ocrSettings; }, [ocrSettings]);

  const startCamera = useCallback(async (facingMode = selectedCamera) => {
    try {
      stopCameraStream(streamRef.current);
      const stream = isMobile
        ? await requestMobileCameraStream(facingMode)
        : await requestCameraStream(facingMode);
      streamRef.current = stream;
      setIsStreaming(true);
      setMobileOcrResult(null);
    } catch (error) {
      toast.error(error.message || 'No se pudo acceder a la cámara');
    }
  }, [selectedCamera, isMobile]);

  useEffect(() => {
    if (isStreaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isStreaming]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { stopCameraStream(streamRef.current); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (ocrIntervalRef.current) clearInterval(ocrIntervalRef.current);
    ocrIntervalRef.current = null;
    setIsStreaming(false);
    setAutoOCR(false);
  }, []);

  useEffect(() => { return () => { stopCamera(); }; }, [stopCamera]);

  const captureFrame = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return null;
    const v = videoRef.current;
    if (v.readyState < 2 || v.videoWidth === 0) return null;
    const s = ocrSettingsRef.current || {};
    const w = s.resolution || 480;
    const q = (s.quality || 50) / 100;
    const c = canvasRef.current;
    c.width = w;
    c.height = Math.round(w * v.videoHeight / v.videoWidth);
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', q);
  }, []);

  const processAction = useCallback((plate) => {
    const currentRole = roleRef.current;
    let actionType = currentRole;
    if (currentRole === 'both') {
      actionType = insidePlatesRef.current?.includes(plate) ? 'exit' : 'entry';
    }
    onPlateActionRef.current?.({ type: actionType, plate, entryType: 'kiosk-auto' });
  }, []);

  const runOcrRequest = useCallback(async (img, { roiOnly }) => {
    const s = ocrSettingsRef.current || {};
    const minConf = (s.sensitivity || 25) / 100;
    const { data } = await axios.post(
      `${API}/detect/ocr`,
      { image: img, min_confidence: minConf, roi_only: roiOnly },
      { withCredentials: true, timeout: OCR_REQUEST_TIMEOUT_MS },
    );
    return data;
  }, []);

  const runMobilePlateRead = useCallback(async () => {
    if (isOcrRunningRef.current) return;
    if (ocrEngineStatus?.loading) {
      toast.message('Inicializando lector de patentes...');
      return;
    }
    const img = capturePlateRoiFromVideo(videoRef.current, videoContainerRef.current, 0.93);
    if (!img) {
      toast.error('No se pudo capturar la patente. Revisá la cámara.');
      return;
    }
    isOcrRunningRef.current = true;
    setIsOcrRunning(true);
    setMobileOcrResult(null);
    try {
      const data = await runOcrRequest(img, { roiOnly: true });
      if (data.plates?.length > 0) {
        const best = data.plates[0];
        setMobileOcrResult(best);
        setMobileEditPlate(best.plate || '');
        setDetectedPlate(best.plate || '');
        setDetectedConf(best.confidence || 0);
        if (best.validation === 'INVALID') {
          toast.error('Patente no reconocida — acercate y volvé a intentar');
        } else if (best.validation === 'POSSIBLE') {
          toast.message('Revisá la patente antes de confirmar');
        }
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.error('Patente no reconocida — acercate y volvé a intentar');
        setMobileOcrResult({ validation: 'INVALID', plate: '', confidence: 0 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en OCR');
    }
    isOcrRunningRef.current = false;
    setIsOcrRunning(false);
  }, [runOcrRequest, ocrEngineStatus]);

  const confirmMobilePlate = useCallback(() => {
    const plate = (mobileEditPlate || '').trim().toUpperCase();
    if (!plate) return;
    if (mobileOcrResult?.validation === 'INVALID') {
      toast.error('Patente no reconocida — corregila o volvé a leer');
      return;
    }
    processAction(plate);
    setMobileOcrResult(null);
    setDetectedPlate('');
  }, [mobileEditPlate, mobileOcrResult, processAction]);

  const runOCR = useCallback(async () => {
    if (isMobile) {
      await runMobilePlateRead();
      return;
    }
    if (isOcrRunningRef.current) return;
    const img = captureFrame();
    if (!img) return;
    isOcrRunningRef.current = true;
    setIsOcrRunning(true);
    try {
      const data = await runOcrRequest(img, { roiOnly: false });
      if (data.plates?.length > 0) {
        const best = data.plates[0];
        setDetectedPlate(best.plate);
        setDetectedConf(best.confidence);
        setCorrectedPlate(best.plate);
        const ocrBlocked = ocrBlockedPlatesRef?.current?.has(best.plate);
        const startCountdown = shouldStartOcrCountdown({
          autoOcrEnabled: autoOCRRef.current,
          plate: best.plate,
          isBlocked: ocrBlocked,
          countdownRunning: Boolean(countdownRef.current),
          pendingPlate: pendingPlateRef.current,
        });
        if (startCountdown) {
          pendingPlateRef.current = best.plate;
          setCountdown(5);
          countdownRef.current = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
                const plate = pendingPlateRef.current;
                if (plate) {
                  processAction(plate);
                  pendingPlateRef.current = null;
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else if (!autoOCRRef.current) {
          setShowCorrection(true);
        }
      }
    } catch (err) {
      if (!autoOCRRef.current) {
        toast.error(err.response?.data?.detail || 'Error en OCR');
      }
    }
    isOcrRunningRef.current = false;
    setIsOcrRunning(false);
  }, [captureFrame, processAction, ocrBlockedPlatesRef, isMobile, runMobilePlateRead, runOcrRequest]);

  // Auto OCR polling — stable interval, no dependency on runOCR changing
  useEffect(() => {
    if (isMobile) {
      if (ocrIntervalRef.current) {
        clearInterval(ocrIntervalRef.current);
        ocrIntervalRef.current = null;
      }
      return undefined;
    }
    if (autoOCR && isStreaming) {
      const id = setInterval(() => { runOCR(); }, ocrInterval);
      ocrIntervalRef.current = id;
      return () => clearInterval(id);
    }
    if (ocrIntervalRef.current) {
      clearInterval(ocrIntervalRef.current);
      ocrIntervalRef.current = null;
    }
    return undefined;
  }, [autoOCR, isStreaming, ocrInterval, runOCR, isMobile]);

  const roleLabel = role === 'entry' ? 'ENTRADA' : role === 'exit' ? 'SALIDA' : 'ENTRADA / SALIDA';

  return (
    <div
      className={`flex flex-col ${isMobile ? 'min-h-[52dvh]' : 'h-full'}`}
      data-testid={`camera-lane-${laneId}`}
    >
      {/* Config bar */}
      <div className={`flex items-center gap-2 p-2 bg-slate-800 border-b border-slate-700 flex-shrink-0 ${isMobile ? 'flex-wrap' : ''}`}>
        <select value={role} onChange={(e) => setRole(e.target.value)}
          className={`bg-slate-700 text-sm font-bold rounded-lg px-3 py-1.5 shrink-0 max-w-[38%] sm:max-w-none ${role === 'entry' ? 'text-emerald-400 border border-emerald-500/50' : role === 'exit' ? 'text-red-400 border border-red-500/50' : 'text-blue-400 border border-blue-500/50'}`}
          data-testid={`role-select-${laneId}`}>
          <option value="entry">ENTRADA</option>
          <option value="exit">SALIDA</option>
          <option value="both">AMBOS</option>
        </select>
        <select value={selectedCamera}
          onChange={(e) => {
            const facingMode = e.target.value;
            setSelectedCamera(facingMode);
            if (isStreaming) startCamera(facingMode);
          }}
          className="bg-slate-700 border border-slate-600 text-slate-200 text-xs rounded-lg px-2 py-1.5 flex-1 min-w-0"
          data-testid={`camera-select-${laneId}`}>
          {CAMERA_OPTIONS.map((camera) => (
            <option key={camera.value} value={camera.value}>{camera.label}</option>
          ))}
        </select>
        {isStreaming && (
          <Button size="sm" onClick={stopCamera} className="bg-red-600 hover:bg-red-700 h-7 px-2" data-testid={`stop-${laneId}`}>
            <CameraOff className="w-3 h-3" />
          </Button>
        )}
      </div>

      {/* Video area */}
      <div
        ref={videoContainerRef}
        className={`flex-1 relative bg-slate-900 ${isMobile ? 'min-h-[240px]' : 'min-h-0'}`}
      >
        {isStreaming ? (
          <>
            <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid={`video-${laneId}`} />
            <canvas ref={canvasRef} className="hidden" />
            {isMobile && (
              <div className="absolute inset-0 z-[5] pointer-events-none flex flex-col items-center justify-center px-3 pb-2">
                <p className="text-white/90 text-xs sm:text-sm font-medium mb-2 text-center drop-shadow-md px-2">
                  Ubicá la patente dentro del recuadro
                </p>
                <div
                  className="w-[88%] max-w-md border-2 border-amber-400/90 rounded-md bg-slate-950/20 flex items-center justify-center"
                  style={{ aspectRatio: '4.5 / 1', maxHeight: '28%' }}
                >
                  <span className="text-amber-200/40 font-mono text-base sm:text-lg tracking-[0.25em] sm:tracking-[0.35em] select-none">AB 123 CD</span>
                </div>
                <div className="absolute inset-0 bg-slate-950/40 -z-10" aria-hidden />
              </div>
            )}
            {!isMobile && ocrEngineStatus && !ocrEngineStatus.ready && (
              <div className="absolute inset-x-0 top-14 z-10 flex justify-center px-3">
                <Badge className="bg-slate-800/95 text-amber-300 border border-amber-500/50 px-3 py-2">
                  Inicializando lector de patentes...
                </Badge>
              </div>
            )}
            {/* Role badge — desktop only (mobile uses the config bar select) */}
            {!isMobile && (
              <div className="absolute top-2 left-2 z-10">
                <Badge className={`text-sm font-bold px-3 py-1 ${role === 'entry' ? 'bg-emerald-600' : role === 'exit' ? 'bg-red-600' : 'bg-blue-600'}`}>
                  {roleLabel}
                </Badge>
              </div>
            )}
            {/* OCR running indicator */}
            {isOcrRunning && (
              <div className="absolute top-2 right-2 z-10">
                <Badge className="bg-amber-600 text-base px-3 py-1">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />Leyendo patente...
                </Badge>
              </div>
            )}
            {/* Detected plate with confidence + countdown */}
            {detectedPlate && (
              <div className="absolute bottom-3 left-3 right-3 z-10">
                <div className="bg-slate-900/95 rounded-lg p-2 border border-amber-500/50">
                  <div className="flex items-center justify-between">
                    <Badge className="bg-amber-600/90 text-lg px-3 py-1 font-mono">
                      <ScanLine className="w-4 h-4 mr-2" />{detectedPlate}
                    </Badge>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={`${detectedConf >= 0.7 ? 'text-emerald-400 border-emerald-500' : detectedConf >= 0.4 ? 'text-amber-400 border-amber-500' : 'text-red-400 border-red-500'}`}>
                        {Math.round(detectedConf * 100)}%
                      </Badge>
                      {countdown > 0 && (
                        <Badge className="bg-blue-600 animate-pulse font-mono">{countdown}s</Badge>
                      )}
                    </div>
                  </div>
                  {/* Countdown mode — Corregir button */}
                  {countdown > 0 && autoOCR && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <Input value={correctedPlate} onChange={(e) => setCorrectedPlate(e.target.value.toUpperCase())}
                        className="bg-slate-800 border-slate-600 text-white font-mono h-8 text-sm uppercase flex-1" />
                      <Button size="sm" onClick={() => {
                        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                        pendingPlateRef.current = null;
                        setCountdown(0);
                        processAction(correctedPlate);
                      }} className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2 text-xs">Corregir</Button>
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
                        pendingPlateRef.current = null;
                        setCountdown(0); setDetectedPlate('');
                      }} className="text-red-400 h-8 px-2 text-xs">Cancelar</Button>
                    </div>
                  )}
                  {/* Manual mode — correction */}
                  {showCorrection && !autoOCR && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <Input value={correctedPlate} onChange={(e) => setCorrectedPlate(e.target.value.toUpperCase())}
                        className="bg-slate-800 border-slate-600 text-white font-mono h-8 text-sm uppercase flex-1" />
                      <Button size="sm" onClick={() => { processAction(correctedPlate); setShowCorrection(false); }} className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2 text-xs">OK</Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowCorrection(false)} className="text-slate-400 h-8 px-2 text-xs">X</Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Camera className="w-16 h-16 text-slate-700" />
            <Button onClick={() => startCamera()} className="bg-blue-600 hover:bg-blue-700" data-testid={`start-${laneId}`}>
              <Camera className="w-4 h-4 mr-2" />Iniciar Camara
            </Button>
          </div>
        )}
      </div>

      {/* OCR controls below video */}
      {isStreaming && (
        <div className="p-2 space-y-2 bg-slate-800/50 border-t border-slate-700 flex-shrink-0">
          {isMobile ? (
            <>
              {ocrEngineStatus?.error && (
                <p className="text-center text-red-300 text-sm py-1">Lector OCR no disponible. Revisá el servidor o probá de nuevo.</p>
              )}
              {(ocrEngineStatus?.loading || (isOcrRunning && !ocrEngineStatus?.ready)) && (
                <p className="text-center text-amber-300 text-sm py-1">
                  {ocrEngineStatus?.loading ? 'Inicializando lector de patentes...' : 'Cargando modelos OCR, aguardá...'}
                </p>
              )}
              <Button
                onClick={runMobilePlateRead}
                disabled={isOcrRunning || Boolean(ocrEngineStatus?.loading)}
                className="w-full h-14 text-lg font-bold bg-amber-500 hover:bg-amber-600 text-slate-950"
                data-testid={`manual-ocr-${laneId}`}
              >
                {isOcrRunning ? <Loader2 className="w-6 h-6 animate-spin mr-2" /> : <ScanLine className="w-6 h-6 mr-2" />}
                LEER PATENTE
              </Button>
              {mobileOcrResult && (
                <div className="rounded-lg border border-slate-600 bg-slate-900/90 p-3 space-y-2">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Patente detectada</p>
                  <Input
                    value={mobileEditPlate}
                    onChange={(e) => setMobileEditPlate(e.target.value.toUpperCase())}
                    className="font-mono text-2xl h-14 text-center uppercase tracking-widest bg-slate-800 border-slate-600"
                    data-testid={`mobile-plate-edit-${laneId}`}
                  />
                  {mobileOcrResult.confidence != null && (
                    <p className={`text-sm ${mobileOcrResult.validation === 'VALID' ? 'text-emerald-400' : mobileOcrResult.validation === 'POSSIBLE' ? 'text-amber-400' : 'text-red-400'}`}>
                      Confianza {Math.round((mobileOcrResult.confidence || 0) * 100)}%
                      {mobileOcrResult.validation === 'POSSIBLE' ? ' — confirmá o corregí' : ''}
                    </p>
                  )}
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <Button
                      onClick={confirmMobilePlate}
                      disabled={!mobileEditPlate.trim() || mobileOcrResult.validation === 'INVALID'}
                      className="h-12 bg-emerald-600 hover:bg-emerald-700 font-bold"
                      data-testid={`mobile-use-plate-${laneId}`}
                    >
                      Usar patente
                    </Button>
                    <Button variant="outline" onClick={runMobilePlateRead} disabled={isOcrRunning} className="h-12 border-slate-600">
                      Volver a leer
                    </Button>
                    <Button variant="secondary" onClick={() => setMobileEditPlate(mobileOcrResult.plate || '')} className="h-12">
                      Corregir
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-between flex-1 p-2 bg-emerald-900/30 border border-emerald-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Zap className={`w-4 h-4 ${autoOCR ? 'text-emerald-400' : 'text-slate-500'}`} />
                    <span className="text-slate-300 text-xs sm:text-sm">Auto OCR (1s)</span>
                  </div>
                  <Switch checked={autoOCR} onCheckedChange={setAutoOCR} data-testid={`auto-ocr-${laneId}`} />
                </div>
                <Button size="sm" onClick={async () => {
                  const img = captureFrame();
                  if (!img) return;
                  try {
                    await axios.post(`${API}/ocr/save-for-review`, { image: img }, { withCredentials: true });
                    toast.success('Foto enviada a Revision OCR');
                  } catch { toast.error('Error al guardar'); }
                }} className="bg-purple-600 hover:bg-purple-700 h-9 px-2 text-[10px] sm:text-xs whitespace-nowrap" data-testid={`review-ocr-${laneId}`}>
                  Revisar OCR
                </Button>
              </div>
              {autoOCR && (
                <p className="text-xs text-emerald-400 px-1">
                  {role === 'entry' ? 'Detecta patente y registra ENTRADA auto.' :
                   role === 'exit' ? 'Detecta patente y registra SALIDA auto.' :
                   'Lee patente cada 1s. Si esta adentro cobra salida, si no registra entrada.'}
                </p>
              )}
              {!autoOCR && (
                <Button onClick={runOCR} disabled={isOcrRunning} size="sm" className="w-full bg-amber-600 hover:bg-amber-700" data-testid={`manual-ocr-${laneId}`}>
                  {isOcrRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanLine className="w-4 h-4 mr-2" />}
                  Leer Patente (OCR)
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {/* Last action notification */}
      {lastAction && (
        <div className={`p-3 flex-shrink-0 ${
          lastAction.barrier ? 'bg-emerald-900/70 border-t-2 border-emerald-400' :
          lastAction.type === 'entry' ? 'bg-emerald-900/50 border-t border-emerald-600' : 'bg-blue-900/50 border-t border-blue-600'
        }`} data-testid={`action-${laneId}`}>
          {lastAction.barrier && (
            <div className="text-center py-1.5 bg-emerald-600 rounded mb-2 animate-pulse" data-testid={`barrier-${laneId}`}>
              <p className="text-lg font-black text-white tracking-widest">BARRERA ABIERTA</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {lastAction.type === 'entry' ? <LogIn className="w-5 h-5 text-emerald-400" /> : <LogOutIcon className="w-5 h-5 text-blue-400" />}
            <span className={`font-bold ${lastAction.type === 'entry' ? 'text-emerald-400' : 'text-blue-400'}`}>
              {lastAction.type === 'entry' ? 'ENTRADA' : 'SALIDA'}
            </span>
            <span className="text-2xl font-mono font-bold text-white">{lastAction.plate}</span>
            {lastAction.is_member && <Badge className="bg-blue-600 text-xs">SOCIO</Badge>}
          </div>
          {lastAction.type === 'exit' && !lastAction.is_member && !lastAction.paid_at_entry && (
            <p className="text-xl font-bold text-emerald-400 mt-1">{formatCurrency(lastAction.amount)}</p>
          )}
          {lastAction.type === 'exit' && lastAction.paid_at_entry && <p className="text-amber-400 text-sm mt-1">Ya pagado en entrada</p>}
          {lastAction.type === 'entry' && lastAction.paid_at_entry && (
            <p className="text-amber-400 font-bold mt-1">Cobrado: {formatCurrency(lastAction.amount_charged)}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────── Main Kiosk Page ─────────── */
export default function KioskPage() {
  const navigate = useNavigate();
  const [vehiclesInside, setVehiclesInside] = useState([]);
  const [tariff, setTariff] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [plateInput, setPlateInput] = useState('');
  const [vehicleType, setVehicleType] = useState('auto');
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrPlate, setQrPlate] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [dialogPlate, setDialogPlate] = useState('');
  const [entryType, setEntryType] = useState('kiosk');
  const [lastAction, setLastAction] = useState(null);
  const [showVehiclesList, setShowVehiclesList] = useState(false);
  const ocrBlockedPlatesRef = useRef(new Set());
  const dialogPlateRef = useRef('');
  const dialogSuccessRef = useRef(false);
  const [ocrSettings, setOcrSettings] = useState({
    sensitivity: 25,
    quality: 70,
    resolution: 640,
    interval: 1500,
  });
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  const [ocrEngineStatus, setOcrEngineStatus] = useState(null);

  const insidePlates = vehiclesInside.map((v) => v.plate);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchOcrStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/detect/ocr-status`, { withCredentials: true });
        if (!cancelled) setOcrEngineStatus(data);
      } catch {
        /* ignore */
      }
    };
    const warmAndPoll = async () => {
      try {
        await axios.post(`${API}/detect/ocr-warmup`, null, { withCredentials: true });
      } catch {
        /* ignore */
      }
      await fetchOcrStatus();
    };
    warmAndPoll();
    const timer = setInterval(fetchOcrStatus, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const refreshData = useCallback(async () => {
    try {
      const [vehiclesRes, tariffRes, eventsRes] = await Promise.all([
        axios.get(`${API}/vehicles/inside`, { withCredentials: true }),
        axios.get(`${API}/tariff`, { withCredentials: true }),
        axios.get(`${API}/event-types?only_active=true`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVehiclesInside(vehiclesRes.data);
      setTariff(tariffRes.data);
      setEventTypes(eventsRes.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 10000);
    if (parkingChannel) parkingChannel.onmessage = () => refreshData();
    return () => { clearInterval(interval); if (parkingChannel) parkingChannel.onmessage = null; };
  }, [refreshData]);

  const blockPlateForOcr = useCallback((plate, durationMs) => {
    if (!plate) return;
    ocrBlockedPlatesRef.current.add(plate);
    if (durationMs) {
      setTimeout(() => ocrBlockedPlatesRef.current.delete(plate), durationMs);
    }
  }, []);

  const unblockPlateForOcr = useCallback((plate) => {
    if (plate) ocrBlockedPlatesRef.current.delete(plate);
  }, []);

  const handleDialogClose = useCallback((open, plate) => {
    if (!open && plate && !dialogSuccessRef.current) {
      unblockPlateForOcr(plate);
    }
    if (!open) dialogSuccessRef.current = false;
  }, [unblockPlateForOcr]);

  const handleParkingSuccess = useCallback((data, type) => {
    dialogSuccessRef.current = true;
    blockPlateForOcr(data.plate, 8000);
    setLastAction({
      type,
      plate: data.plate,
      is_member: data.is_member,
      paid_at_entry: data.paid_at_entry,
      amount_charged: data.amount_charged,
      amount: data.amount_charged,
      duration: data.duration_minutes,
      barrier: type === 'exit' || data.is_member || data.paid_at_entry,
    });
    setTimeout(() => setLastAction(null), 8000);
    setPlateInput('');
    refreshData();
    parkingChannel?.postMessage({ type: 'update' });
  }, [refreshData, blockPlateForOcr]);

  const openEntryDialog = useCallback((plate, type = 'kiosk') => {
    dialogPlateRef.current = plate;
    dialogSuccessRef.current = false;
    blockPlateForOcr(plate);
    setDialogPlate(plate);
    setEntryType(type);
    setShowEntryDialog(true);
  }, [blockPlateForOcr]);

  const openExitDialog = useCallback((plate) => {
    dialogPlateRef.current = plate;
    dialogSuccessRef.current = false;
    blockPlateForOcr(plate);
    setDialogPlate(plate);
    setShowExitDialog(true);
  }, [blockPlateForOcr]);

  const handlePlateAction = useCallback(({ type, plate, entryType: et }) => {
    if (type === 'exit') {
      openExitDialog(plate);
    } else {
      openEntryDialog(plate, et || 'kiosk-auto');
    }
  }, [openEntryDialog, openExitDialog]);

  const handleManualEntry = () => {
    if (!plateInput.trim()) return;
    openEntryDialog(plateInput.toUpperCase(), 'kiosk');
  };

  const KEYS_ROW1 = ['A','B','C','D','E','F','G','H'];
  const KEYS_ROW2 = ['I','J','K','L','M','N','O','P'];
  const KEYS_ROW3 = ['Q','R','S','T','U','V','W','X'];
  const KEYS_ROW4 = ['Y','Z','0','1','2','3','4','5'];
  const KEYS_ROW5 = ['6','7','8','9','DEL','CLR'];

  return (
    <div className="h-[100dvh] bg-slate-950 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-3 sm:px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="ParkingVision AI" className="h-6 sm:h-8 w-auto" />
            <h1 className="text-sm sm:text-lg font-bold text-white hidden sm:block">ParkingVision AI</h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-right">
              <p className="text-lg sm:text-2xl font-bold text-white font-mono" data-testid="kiosk-time">
                {currentTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
              <p className="text-[10px] sm:text-xs text-slate-400">{currentTime.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowVehiclesList((v) => !v)}
              className="bg-slate-800 rounded-lg px-2 sm:px-3 py-1 hover:bg-slate-700 transition-colors"
              data-testid="vehicles-count"
            >
              <p className="text-[10px] sm:text-xs text-slate-400">Adentro</p>
              <p className="text-xl sm:text-2xl font-bold text-emerald-400">{vehiclesInside.length}</p>
            </button>
            <Button variant="ghost" size="sm" onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()} className="text-slate-400 hidden sm:flex"><Maximize2 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} className={`${showSettings ? 'text-blue-400' : 'text-slate-400'} hidden md:inline-flex`} data-testid="settings-btn"><Settings className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-400" data-testid="kiosk-nav-back"><X className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <div className="flex flex-1 flex-col min-h-0 lg:flex-row lg:overflow-hidden">
        {/* Camera area */}
        <div className={`min-h-0 flex flex-col ${isMobile ? 'shrink-0' : 'flex-1 min-h-[320px] sm:min-h-[420px] lg:min-h-0'}`}>
          <CameraLane laneId="single" defaultRole="both" onPlateAction={handlePlateAction} insidePlates={insidePlates} ocrBlockedPlatesRef={ocrBlockedPlatesRef} ocrInterval={ocrSettings.interval} ocrSettings={ocrSettings} isMobile={isMobile} ocrEngineStatus={ocrEngineStatus} />
        </div>

        {/* Side Panel */}
        <div className={`w-full lg:w-72 xl:w-80 bg-slate-900 border-t lg:border-t-0 lg:border-l border-slate-800 p-3 flex flex-col min-h-0 overflow-y-auto flex-1 lg:flex-none flex-shrink-0 ${isMobile ? 'order-3' : ''}`}>
          <div className="mb-3 flex-shrink-0">
            <label className="text-slate-400 text-xs mb-1 block">Patente Manual</label>
            <Input value={plateInput} onChange={(e) => setPlateInput(e.target.value.toUpperCase())} placeholder={platePlaceholder(vehicleType)}
              className="bg-slate-800 border-slate-700 text-white text-xl sm:text-2xl font-mono text-center h-12 sm:h-14 uppercase tracking-widest"
              data-testid="kiosk-plate-input" />
          </div>
          <div className="mb-2">
            <label className="text-slate-400 text-xs mb-1 block">Tipo</label>
            <Select value={vehicleType} onValueChange={setVehicleType}>
              <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-9" data-testid="kiosk-side-vehicle-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="auto"><span className="flex items-center gap-1"><Car className="w-3 h-3" />Auto</span></SelectItem>
                <SelectItem value="moto"><span className="flex items-center gap-1"><Bike className="w-3 h-3" />Moto</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-shrink-0">
            <Button onClick={handleManualEntry} disabled={!plateInput.trim()} className="h-11 w-full bg-emerald-600 text-base font-bold hover:bg-emerald-700 sm:h-12 sm:text-lg" data-testid="kiosk-entry-btn">
              <LogIn className="w-5 h-5 mr-2" />ENTRADA
            </Button>
          </div>
          {/* Vehicles inside — quick cobro */}
          {showVehiclesList && vehiclesInside.length > 0 && (
            <div className="mt-3 p-2 bg-slate-800 rounded-lg border border-slate-700 max-h-40 overflow-y-auto" data-testid="kiosk-vehicles-list">
              <p className="text-[10px] uppercase text-slate-500 mb-1.5 font-bold">Vehiculos adentro</p>
              {vehiclesInside.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-1 py-1 border-b border-slate-700/50 last:border-0">
                  <div className="min-w-0">
                    <span className="font-mono text-sm text-white">{v.plate}</span>
                    <span className="text-[10px] text-slate-500 ml-1">{formatDuration(v.duration_minutes)}</span>
                    {v.vehicle_type === 'moto' ? <Bike className="w-3 h-3 inline ml-1 text-amber-400" /> : <Car className="w-3 h-3 inline ml-1 text-blue-400" />}
                    {v.is_member && <Badge className="ml-1 text-[9px] py-0 bg-blue-600">S</Badge>}
                  </div>
                  <Button size="sm" onClick={() => openExitDialog(v.plate)} className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 shrink-0" data-testid={`kiosk-finalize-${v.plate}`}>
                    Finalizar
                  </Button>
                </div>
              ))}
            </div>
          )}
          {/* Keyboard - hidden on very small screens */}
          {showSettings && (
            <div className="mt-3 p-3 bg-slate-800 rounded-lg border border-slate-700 space-y-4 flex-shrink-0" data-testid="ocr-settings-panel">
              <p className="text-xs font-bold text-slate-300 uppercase tracking-wider">Ajustes OCR</p>
              <div>
                <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Sensibilidad</span><span className="text-white font-mono">{ocrSettings.sensitivity}%</span></div>
                <Slider value={[ocrSettings.sensitivity]} min={5} max={80} step={5} onValueChange={([v]) => setOcrSettings(s => ({...s, sensitivity: v}))} className="w-full" />
                <p className="text-[10px] text-slate-500 mt-0.5">{ocrSettings.sensitivity <= 20 ? 'Muy sensible (mas detecciones, mas errores)' : ocrSettings.sensitivity >= 50 ? 'Estricto (menos errores, puede fallar)' : 'Equilibrado'}</p>
              </div>
              <div>
                <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Calidad imagen</span><span className="text-white font-mono">{ocrSettings.quality}%</span></div>
                <Slider value={[ocrSettings.quality]} min={20} max={90} step={10} onValueChange={([v]) => setOcrSettings(s => ({...s, quality: v}))} className="w-full" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Resolucion</span><span className="text-white font-mono">{ocrSettings.resolution}px</span></div>
                <Slider value={[ocrSettings.resolution]} min={320} max={640} step={40} onValueChange={([v]) => setOcrSettings(s => ({...s, resolution: v}))} className="w-full" />
              </div>
              <div>
                <div className="flex justify-between text-xs text-slate-400 mb-1"><span>Intervalo lectura</span><span className="text-white font-mono">{ocrSettings.interval >= 1000 ? `${ocrSettings.interval/1000}s` : `${ocrSettings.interval}ms`}</span></div>
                <Slider value={[ocrSettings.interval]} min={500} max={3000} step={250} onValueChange={([v]) => setOcrSettings(s => ({...s, interval: v}))} className="w-full" />
              </div>
            </div>
          )}
          <div className="mt-3 space-y-1 flex-shrink-0 hidden sm:block">
            {[KEYS_ROW1, KEYS_ROW2, KEYS_ROW3, KEYS_ROW4, KEYS_ROW5].map((row, ri) => (
              <div key={ri} className="grid gap-1" style={{ gridTemplateColumns: `repeat(${row.length}, 1fr)` }}>
                {row.map((key) => (
                  <Button key={key} variant="outline" size="sm"
                    onClick={() => { if (key==='DEL') setPlateInput(p=>p.slice(0,-1)); else if (key==='CLR') setPlateInput(''); else setPlateInput(p=>p+key); }}
                    className={`h-9 text-sm font-bold border-slate-700 ${key==='DEL' ? 'bg-amber-800 text-amber-200 col-span-1' : key==='CLR' ? 'bg-red-800 text-red-200' : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                    data-testid={`kiosk-key-${key}`}>{key}</Button>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-auto pt-3 flex items-center justify-center gap-2 flex-shrink-0">
            <img src={DISRUPTIVE_LOGO} alt="Disruptive" className="h-4 w-auto invert" />
            <span className="text-slate-600 text-xs">Disruptive Software</span>
          </div>
        </div>
      </div>

      {/* Global last action banner */}
      {lastAction && (
        <div className={`p-3 flex-shrink-0 ${
          lastAction.barrier ? 'bg-emerald-900/70 border-t-2 border-emerald-400' :
          lastAction.type === 'entry' ? 'bg-emerald-900/50 border-t border-emerald-600' : 'bg-blue-900/50 border-t border-blue-600'
        }`} data-testid="kiosk-last-action">
          {lastAction.barrier && (
            <div className="text-center py-1.5 bg-emerald-600 rounded mb-2 animate-pulse">
              <p className="text-lg font-black text-white tracking-widest">BARRERA ABIERTA</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <span className={`font-bold ${lastAction.type === 'entry' ? 'text-emerald-400' : 'text-blue-400'}`}>
              {lastAction.type === 'entry' ? 'ENTRADA' : 'SALIDA'}
            </span>
            <span className="text-2xl font-mono font-bold text-white">{lastAction.plate}</span>
            {lastAction.is_member && <Badge className="bg-blue-600 text-xs">SOCIO</Badge>}
            {lastAction.type === 'exit' && (
              <span className={`text-xl font-bold ${lastAction.is_member || lastAction.paid_at_entry ? 'text-amber-400' : 'text-emerald-400'}`}>
                {getExitChargeLabel(lastAction)}
              </span>
            )}
          </div>
        </div>
      )}

      <ParkingEntryDialog
        open={showEntryDialog}
        onOpenChange={(open) => {
          handleDialogClose(open, dialogPlateRef.current);
          setShowEntryDialog(open);
        }}
        initialPlate={dialogPlate}
        initialVehicleType={vehicleType}
        tariff={tariff}
        eventTypes={eventTypes}
        entryType={entryType}
        onSuccess={(data) => handleParkingSuccess(data, 'entry')}
      />

      <ParkingExitDialog
        open={showExitDialog}
        onOpenChange={(open) => {
          handleDialogClose(open, dialogPlateRef.current);
          setShowExitDialog(open);
        }}
        plate={dialogPlate}
        onSuccess={(data) => handleParkingSuccess(data, 'exit')}
      />

      {/* QR Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm text-center">
          <DialogHeader><DialogTitle className="text-xl font-bold flex items-center justify-center gap-2"><QrCode className="w-5 h-5 text-emerald-400" />QR Ticket — {qrPlate}</DialogTitle></DialogHeader>
          <div className="mt-4">
            {qrPlate && <img src={`${API}/vehicles/qr/${qrPlate}`} alt={`QR ${qrPlate}`} className="mx-auto w-64 h-64 rounded bg-white p-2" data-testid="qr-image" />}
            <p className="text-slate-400 text-sm mt-4">Presente este QR al salir</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
